import { api } from '../services/api';
import { LinkPreviewData } from './social-links';
import { createDeduplicatedTaskQueue } from './deduplicated-task-queue';

const MAX_CONCURRENT_PREVIEWS = 3;
const previewByUrl = new Map<string, LinkPreviewData | null>();
const previewByPostId = new Map<number, LinkPreviewData | null>();
const attemptedPostIds = new Set<number>();
const inFlightPostRequests = new Map<number, Promise<LinkPreviewData | null>>();
const enqueuePreview = createDeduplicatedTaskQueue<LinkPreviewData | null>(MAX_CONCURRENT_PREVIEWS);

/**
 * Hydrates legacy posts that predate posts.link_preview.
 *
 * A post is attempted at most once per page session. Requests for the same URL
 * are coalesced and the shared queue permits only three API calls at a time.
 */
export const requestLegacyPostLinkPreview = (
  postId: number,
  url: string,
  token?: string,
): Promise<LinkPreviewData | null> => {
  if (!token) return Promise.resolve(null);

  const knownPreview = previewByPostId.get(postId);
  if (knownPreview !== undefined) return Promise.resolve(knownPreview);

  const pendingPostRequest = inFlightPostRequests.get(postId);
  if (pendingPostRequest) return pendingPostRequest;

  if (attemptedPostIds.has(postId)) return Promise.resolve(null);
  attemptedPostIds.add(postId);

  const request = (async () => {
    const cachedPreview = previewByUrl.get(url);
    if (cachedPreview !== undefined) return cachedPreview;

    const preview = await enqueuePreview(url, async () => {
      try {
        const { data } = await api(token).post('/posts/link-preview', { url });
        return data?.preview ?? null;
      } catch {
        return null;
      }
    });

    previewByUrl.set(url, preview);
    return preview;
  })();

  inFlightPostRequests.set(postId, request);

  return request.then(preview => {
    previewByPostId.set(postId, preview);
    return preview;
  }).finally(() => {
    inFlightPostRequests.delete(postId);
  });
};
